import { useQuery } from '@tanstack/react-query';

import { unwrap } from '@/api/envelope';
import { listArcContent, type ArcResourceType } from '@/api/generated';

import { arcContentKey } from './keys';
import { useArcOrganizationId } from './useArc';

/** How many results the picker shows at a time. */
export const CONTENT_PAGE_SIZE = 20;

/**
 * One page of the organization's published content of one type, for the Add
 * Content picker (`GET /arc/content`). `q` is the settled search, not every
 * keystroke: the caller debounces it. A page is kept for a minute, so opening
 * the picker again does not ask again, and the last page stays on screen
 * while the next one is on its way.
 */
export function useContentSearch(type: ArcResourceType | null, q: string, page: number) {
  const organizationId = useArcOrganizationId();
  return useQuery({
    queryKey: arcContentKey(organizationId, type, q, page),
    queryFn: async () => {
      const reply = await listArcContent({
        query: {
          type: type as ArcResourceType,
          q: q || undefined,
          page,
          limit: CONTENT_PAGE_SIZE,
        },
        throwOnError: true,
      });
      return unwrap(reply.data);
    },
    enabled: organizationId !== null && type !== null,
    staleTime: 60_000,
    placeholderData: (previous) => previous,
  });
}
